import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Mic2, Send } from "lucide-react";

export default function InterviewPrep() {
  const [role, setRole] = useState("");
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const start = async () => {
    if (!role.trim()) return;
    setLoading(true);
    try {
      const r = await api.post("/interview/start", { role });
      setSession(r.data.session_id);
      setMessages([{ role: "assistant", text: r.data.reply }]);
    } catch { alert("Failed to start"); }
    finally { setLoading(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!msg.trim() || !session) return;
    const m = msg;
    setMsg("");
    setMessages((prev) => [...prev, { role: "user", text: m }]);
    setLoading(true);
    try {
      const r = await api.post("/interview/message", { session_id: session, role, message: m });
      setMessages((prev) => [...prev, { role: "assistant", text: r.data.reply }]);
    } catch { setMessages((prev) => [...prev, { role: "assistant", text: "Error" }]); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="interview-prep-page">
      {!session ? (
        <div className="jr-card text-center py-12">
          <Mic2 className="w-12 h-12 text-violet-300 mx-auto mb-4" />
          <div className="text-xl font-semibold text-white mb-2">Start a Mock Interview</div>
          <p className="text-zinc-400 text-sm mb-6">Practice with your AI interview coach. 5 questions, real-time feedback.</p>
          <div className="flex gap-3 max-w-md mx-auto">
            <input value={role} onChange={(e) => setRole(e.target.value)} className="jr-input" placeholder="Target role (e.g. Gen AI Engineer)" data-testid="interview-role-input" />
            <button onClick={start} disabled={loading} className="jr-btn-primary" data-testid="start-interview-btn">
              {loading ? "Starting..." : "Begin"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="jr-card h-[60vh] overflow-y-auto space-y-3" data-testid="interview-chat">
            {messages.map((m, i) => (
              <div key={`int-${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${m.role === "user" ? "bg-violet-500/25 text-violet-50" : "bg-white/5 text-zinc-100"}`}>
                  <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">{m.role === "user" ? "You" : "Coach"}</div>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
              </div>
            ))}
            {loading && <div className="text-sm text-zinc-400 jr-pulse">Coach is typing…</div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="flex gap-2">
            <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type your answer..." className="jr-input" data-testid="interview-input" />
            <button type="submit" disabled={loading} className="jr-btn-primary" data-testid="interview-send"><Send className="w-4 h-4" /></button>
          </form>
        </>
      )}
    </div>
  );
}
